/**
 * Leaderboard System - derives the live asset ladder (player + NPC rankers).
 * All ranking math is pure/deterministic; the only stateful side effect is
 * bumping the player's best-ever rank. NPC net worth grows over real time so
 * climbing the board tracks the player's own rising total assets.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { NPC_RANKERS, npcAssetsAt } from '../config/leaderboard.js';
import { getHoldingsValue } from './tradingSystem.js';

export interface LadderEntry {
  id: string;
  /** display name; player entry uses a sentinel handled by the view */
  name: string;
  assets: number;
  isPlayer: boolean;
}

/** Player total assets = cash + current market value of all holdings. */
export function getPlayerAssets(state: GameState): number {
  const holdings = state.exchange ? getHoldingsValue(state.exchange) : 0;
  return state.coins + holdings;
}

/** Elapsed hours since the ladder epoch (>= 0). */
export function elapsedHours(state: GameState, now: number): number {
  const epoch = state.leaderboard?.epoch ?? now;
  return Math.max(0, (now - epoch) / 3_600_000);
}

/**
 * Full sorted ladder (descending assets), player inserted in place.
 * Pure - takes an explicit `now` for deterministic tests.
 */
export function buildLadder(state: GameState, now: number): LadderEntry[] {
  const hours = elapsedHours(state, now);
  const entries: LadderEntry[] = NPC_RANKERS.map(npc => ({
    id: npc.id,
    name: npc.name,
    assets: npcAssetsAt(npc, hours),
    isPlayer: false,
  }));
  entries.push({ id: '__player__', name: '', assets: getPlayerAssets(state), isPlayer: true });
  entries.sort((a, b) => b.assets - a.assets);
  return entries;
}

/** 1-based rank of the player within the full ladder. */
export function getPlayerRank(state: GameState, now: number): number {
  const ladder = buildLadder(state, now);
  return ladder.findIndex(e => e.isPlayer) + 1;
}

/** Total number of ranked competitors (NPCs + the player). */
export function ladderSize(): number {
  return NPC_RANKERS.length + 1;
}

export const leaderboardSystem: TickSystem<GameState, GameAction> = {
  name: 'leaderboard',

  update(state) {
    if (!state.leaderboard) return state;
    const rank = getPlayerRank(state, Date.now());
    const prevBest = state.leaderboard.bestRank;
    // best rank = smallest number ever reached (0 means "unset")
    const newBest = prevBest === 0 ? rank : Math.min(prevBest, rank);
    if (newBest === prevBest) return state;
    return { ...state, leaderboard: { ...state.leaderboard, bestRank: newBest } };
  },
};
